import { useState } from "react";
import axios from "axios";
import { authHeaders } from "../../contexts/AuthContext";
import { Pause, Play, RotateCcw, SkipForward, Settings } from "lucide-react";
import AdamVoicePicker from "../audio/AdamVoicePicker";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function CommissionerControls({ leagueId, draftState, league }) {
  const [loading, setLoading] = useState("");
  const [expanded, setExpanded] = useState(false);
  const [voiceId, setVoiceId] = useState(null);

  const call = async (action, body = {}) => {
    setLoading(action);
    try { await axios.post(`${API}/commissioner/${leagueId}/${action}`, body, { headers: authHeaders() }); }
    catch (err) { alert(err?.response?.data?.detail || `Error: ${action}`); }
    finally { setLoading(""); }
  };

  const state = draftState?.state;
  const isPaused = state === "PAUSED";

  return (
    <div className="bg-[#252525] border-b border-[#2E2E2E] shrink-0">
      <div className="flex items-center gap-2 px-3 py-2">
        <span className="font-display font-bold text-xs uppercase text-[rgba(255,255,255,0.35)] tracking-wider">Commissioner</span>
        <div className="flex items-center gap-1.5 ml-1">
          {/* Pause/Resume - green for resume (positive), subtle for pause */}
          <button data-testid={isPaused ? "resume-draft-btn" : "pause-draft-btn"}
            onClick={() => call(isPaused ? "resume" : "pause")}
            disabled={!!loading || ["LOBBY","SETUP","DRAFT_COMPLETE"].includes(state)}
            className={`flex items-center gap-1 px-3 py-1.5 font-display font-bold text-xs uppercase transition-all disabled:opacity-40 rounded ${
              isPaused
                ? "bg-[#00C853] text-[#111111]"
                : "border border-[rgba(255,255,255,0.12)] text-[rgba(255,255,255,0.50)] hover:text-[#F5F5F5]"}`}>
            {isPaused ? <Play size={11} fill="currentColor" /> : <Pause size={11} />}
            {loading === (isPaused ? "resume" : "pause") ? "..." : isPaused ? "Resume" : "Pause"}
          </button>

          {/* Undo */}
          <button data-testid="undo-sale-btn" onClick={() => call("undo")}
            disabled={!!loading || (draftState?.undo_count ?? 0) >= 2}
            className="flex items-center gap-1 px-3 py-1.5 border border-[rgba(255,255,255,0.10)] hover:border-[rgba(255,255,255,0.20)] text-[rgba(255,255,255,0.50)] hover:text-[#F5F5F5] font-display font-bold text-xs uppercase transition-all disabled:opacity-40 rounded">
            <RotateCcw size={11} />
            Undo ({2 - (draftState?.undo_count || 0)})
          </button>

          {state === "NOMINATION" && (
            <button data-testid="skip-nominator-btn" onClick={() => call("skip-nominator")} disabled={!!loading}
              className="flex items-center gap-1 px-3 py-1.5 border border-[rgba(255,255,255,0.10)] hover:border-[rgba(255,255,255,0.20)] text-[rgba(255,255,255,0.50)] hover:text-[#F5F5F5] font-display font-bold text-xs uppercase transition-all rounded">
              <SkipForward size={11} />
              Skip
            </button>
          )}

          {(state === "BIDDING" || state === "NOMINATION") && (
            <>
              <button onClick={() => call("adjust-timer", { seconds: -15 })} disabled={!!loading}
                data-testid="timer-minus-btn"
                className="px-2 py-1.5 border border-[rgba(255,255,255,0.10)] hover:border-[rgba(255,255,255,0.20)] text-[rgba(255,255,255,0.50)] hover:text-[#F5F5F5] font-display font-bold text-xs transition-all rounded">
                −15s
              </button>
              <button onClick={() => call("adjust-timer", { seconds: 30 })} disabled={!!loading}
                data-testid="timer-plus-btn"
                className="px-2 py-1.5 border border-[rgba(255,255,255,0.10)] hover:border-[rgba(255,255,255,0.20)] text-[rgba(255,255,255,0.50)] hover:text-[#F5F5F5] font-display font-bold text-xs transition-all rounded">
                +30s
              </button>
            </>
          )}
        </div>
        <button onClick={() => setExpanded(!expanded)} data-testid="commissioner-expand-btn"
          className="ml-auto text-[rgba(255,255,255,0.35)] hover:text-[#F5F5F5] transition-colors">
          <Settings size={13} />
        </button>
      </div>

      {expanded && (
        <div className="border-t border-[#2E2E2E] px-3 py-3">
          {/* [ADAM AUDIO batch 2 item 4] Mid-draft voice switch. Same
              component as the lobby; the switch is a next-line handoff for
              every client because the voice is resolved server-side per
              TTS request. */}
          <AdamVoicePicker
            leagueId={leagueId}
            selectedVoiceId={league?.settings?.adam_voice_id}
            isDark
            onSelected={setVoiceId}
          />
          {voiceId && (
            <p data-testid="commissioner-voice-switched" className="font-body text-xs text-[rgba(255,255,255,0.35)] mt-2">
              Adam switches on his next line - anything mid-sentence finishes in the old voice.
            </p>
          )}
        </div>
      )}
    </div>
  );
}
